import dayjs from 'dayjs';

import { ROOM_OPENING_DISCOUNT_MODE } from 'app/typings/core/extranet/inventory/enums/RoomOpeningDiscountMode';

export type AutoInventoryBadgeVariant = 'active' | 'deactivated';

export type PricingRuleBadgeVariant = 'lastMinute' | 'default' | 'deactivated';

export type AutoInventoryBadge = {
  type: 'autoInventory';
  variant: AutoInventoryBadgeVariant;
  color: 'blue';
  tooltip: string;
};

export type PricingRuleBadge = {
  type: 'pricingRule';
  variant: PricingRuleBadgeVariant;
  color: 'blue' | 'gray';
  tooltip: string;
};

export type ManualEditBadge = {
  type: 'manualEdit';
  color: 'blue' | 'gray';
  tooltip: string;
};

export type InventoryBadge =
  AutoInventoryBadge | PricingRuleBadge | ManualEditBadge;

export type AutoInventoryBadgeParams = {
  stockScrapperEnabled: boolean;
  bookingcomId?: number;
  isAutomated?: boolean;
  value?: number;
};

export const getAutoInventoryBadge = (
  params: AutoInventoryBadgeParams
): AutoInventoryBadge | null => {
  const { stockScrapperEnabled, bookingcomId, isAutomated, value } = params;

  if (!stockScrapperEnabled || !bookingcomId) {
    return null;
  }

  const isAutoControlled = isAutomated || value === undefined;

  return isAutoControlled
    ? {
        type: 'autoInventory',
        variant: 'active',
        color: 'blue',
        tooltip: 'Auto inventory activated',
      }
    : {
        type: 'autoInventory',
        variant: 'deactivated',
        color: 'blue',
        tooltip: 'Auto inventory deactivated by manual edition',
      };
};

export type PricingRuleBadgeParams = {
  hasDefaultPricingRule: boolean;
  stockScrapperEnabled: boolean;
  bookingcomId?: number;
  discountMode?: ROOM_OPENING_DISCOUNT_MODE;
  discountPrice?: number;
  lastMinuteDiscount?: number;
  lastMinuteDays?: number;
  date: string;
};

export const getPricingRuleBadge = (
  params: PricingRuleBadgeParams
): PricingRuleBadge | null => {
  const {
    hasDefaultPricingRule,
    stockScrapperEnabled,
    bookingcomId,
    discountMode,
    lastMinuteDiscount,
    lastMinuteDays,
    discountPrice,
    date,
  } = params;

  if (discountMode === undefined) {
    return null;
  }

  const isSyncedWithBookingcom = stockScrapperEnabled && !!bookingcomId;
  const iconColor = isSyncedWithBookingcom ? 'blue' : 'gray';
  const defaultRuleBadge = (
    variant: PricingRuleBadgeVariant,
    tooltip: string
  ): PricingRuleBadge | null =>
    hasDefaultPricingRule
      ? { type: 'pricingRule', variant, color: iconColor, tooltip }
      : null;

  const dateObj = dayjs(date);
  const hasLastMinute =
    lastMinuteDiscount !== undefined && lastMinuteDays !== undefined;
  const isInLastMinuteDiscount =
    hasLastMinute &&
    dateObj.isBefore(dayjs().add(lastMinuteDays + 1, 'days'), 'days') &&
    !dateObj.isBefore(dayjs(), 'days');

  if (
    discountMode === ROOM_OPENING_DISCOUNT_MODE.MANUAL &&
    discountPrice !== undefined
  ) {
    return defaultRuleBadge(
      'deactivated',
      'Default discount deactivated by manual edition'
    );
  }

  // In a hotel with a default discount, the only job that prices a night nobody
  // priced is the auto inventory sync, for a room linked to Booking.com.
  if (
    discountMode === ROOM_OPENING_DISCOUNT_MODE.MANUAL &&
    hasDefaultPricingRule &&
    !isSyncedWithBookingcom
  ) {
    return null;
  }

  if (
    discountMode === ROOM_OPENING_DISCOUNT_MODE.NONE &&
    discountPrice !== undefined
  ) {
    return defaultRuleBadge(
      'deactivated',
      'Default discount deactivated by manual edition of the Room revenue share'
    );
  }

  if (
    discountMode === ROOM_OPENING_DISCOUNT_MODE.LAST_MINUTE_AUTO ||
    isInLastMinuteDiscount
  ) {
    return {
      type: 'pricingRule',
      variant: 'lastMinute',
      color: iconColor,
      tooltip: 'Last minute discount activated',
    };
  }

  if (discountMode === ROOM_OPENING_DISCOUNT_MODE.NONE) {
    return null;
  }

  return defaultRuleBadge('default', 'Default discount activated');
};

export type ManualEditBadgeParams = {
  stockScrapperEnabled: boolean;
  bookingcomId?: number;
  fieldName: 'discount' | 'discountPrice';
  discountMode?: ROOM_OPENING_DISCOUNT_MODE;
  discountPrice?: number;
  discountRate?: number | null;
};

// Pin displayed on the manually edited field: the discount in MANUAL mode (the
// RRS follows it), the room revenue share in NONE mode (the discount follows it).
// A MANUAL opening without discount rate (NDM migration, edits made before the
// rate existed) keeps its RRS when the BAR moves, so its discount is not pinned.
export const getManualEditBadge = (
  params: ManualEditBadgeParams
): ManualEditBadge | null => {
  const {
    stockScrapperEnabled,
    bookingcomId,
    fieldName,
    discountMode,
    discountPrice,
    discountRate,
  } = params;

  const color = stockScrapperEnabled && !!bookingcomId ? 'blue' : 'gray';

  if (
    fieldName === 'discount' &&
    discountMode === ROOM_OPENING_DISCOUNT_MODE.MANUAL &&
    discountPrice !== undefined &&
    discountRate !== undefined &&
    discountRate !== null
  ) {
    return {
      type: 'manualEdit',
      color,
      tooltip:
        'Manually edited discount. This discount stays fixed when your BAR changes.',
    };
  }

  if (
    fieldName === 'discountPrice' &&
    discountMode === ROOM_OPENING_DISCOUNT_MODE.NONE &&
    discountPrice !== undefined
  ) {
    return {
      type: 'manualEdit',
      color,
      tooltip:
        'Manually edited Room revenue share. This rate stays fixed when your BAR changes.',
    };
  }

  return null;
};
